import React, { useEffect } from 'react';
import { useFadeIn } from '../hooks/useFadeIn';
import './PricingPage.css';

interface PricingPageProps {
  onLoginClick?: () => void;
}

const PricingPage: React.FC<PricingPageProps> = ({ onLoginClick }) => {
  const headerFadeIn = useFadeIn({ delay: 0.1 });
  const pricingGridFadeIn = useFadeIn({ delay: 0.2 });
  const customPlanFadeIn = useFadeIn({ delay: 0.3 });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const tierData = [
    {
      name: 'Starter',
      monthlyPrice: '$49',
      profilesLimit: 'Up to 1,000 profiles per month',
      analytics: 'Basic analytics',
      teamSeats: '1 team seat',
      ctaLabel: 'Choose Starter',
    },
    {
      name: 'Growth',
      monthlyPrice: '$149',
      profilesLimit: 'Up to 5,000 profiles per month',
      analytics: 'Advanced analytics',
      teamSeats: 'Up to 3 team seats',
      ctaLabel: 'Choose Growth',
      isFeatured: true,
    },
    {
      name: 'Business',
      monthlyPrice: '$399',
      profilesLimit: 'Up to 20,000 profiles per month',
      analytics: 'Custom analytics & reporting',
      teamSeats: 'Unlimited team seats',
      ctaLabel: 'Choose Business',
    },
  ];

  return (
    <section id="pricing" className="pricing-page landing-section">
      <div className="landing-container">
        <div 
          ref={headerFadeIn.ref} 
          className={`pricing-header ${headerFadeIn.isVisible ? 'fade-in-hero' : ''}`}
          style={{ 
            animationDelay: `${headerFadeIn.delay}s`,
            opacity: headerFadeIn.isVisible ? undefined : 0
          } as React.CSSProperties}
        >
          <div className="pricing-header-content">
            <div className="pricing-title-wrap">
              <h1 className="pricing-title">Pricing</h1>
              <p className="pricing-subtitle">
                Scalable moderation tools designed for communities of any size.
              </p>
            </div>
          </div>
        </div>

        <div 
          ref={pricingGridFadeIn.ref} 
          className={`pricing-grid ${pricingGridFadeIn.isVisible ? 'fade-in-hero' : ''}`}
          style={{ 
            animationDelay: `${pricingGridFadeIn.delay}s`,
            opacity: pricingGridFadeIn.isVisible ? undefined : 0
          } as React.CSSProperties}
        >
          {tierData.map((tier) => {
            const features = [tier.profilesLimit, tier.analytics, tier.teamSeats];
            return (
              <article
                key={tier.name}
                className={`pricing-card ${tier.isFeatured ? 'pricing-card-featured' : ''}`}
              >
                <div className="pricing-card-top">
                  <div className="pricing-card-info">
                    <h3 className="pricing-card-name">{tier.name}</h3>
                  </div>
                </div>

                <div className="pricing-card-price">
                  <span className="price-amount">{tier.monthlyPrice}</span>
                  <span className="price-unit">/month</span>
                </div>

                <ul className="pricing-feature-list">
                  {features.map((feature) => (
                    <li key={feature} className="pricing-feature-item">
                      <span className="pricing-feature-dot" aria-hidden="true" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button className="pricing-card-cta" onClick={onLoginClick}>
                  {tier.ctaLabel}
                </button>
              </article>
            );
          })}
        </div>

        <div 
          ref={customPlanFadeIn.ref} 
          className={`pricing-custom-plan ${customPlanFadeIn.isVisible ? 'fade-in-hero' : ''}`}
          style={{ 
            animationDelay: `${customPlanFadeIn.delay}s`,
            opacity: customPlanFadeIn.isVisible ? undefined : 0
          } as React.CSSProperties}
        >
          <div className="pricing-custom-content">
            <h3>Scaling beyond 20,000 profiles?</h3>
            <p>
              Get a custom plan designed for your team's specific enterprise workflow and volume.
            </p>
          </div>
          <button className="pricing-custom-cta" onClick={onLoginClick}>
            Contact Sales
          </button>
        </div>
      </div>
    </section>
  );
};

export default PricingPage;

